import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import { ticketService } from '../../services/ticketService';
import { useAuth } from '../../contexts/AuthContext';
import CreateTicketModal from './components/CreateTicketModal';
import TicketDetailModal from './components/TicketDetailModal';

const SupportTicketSystemUserInterface = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ticketService?.getUserTickets();
      setTickets(data || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err?.message || 'Destek talepleri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (ticketData) => {
    try {
      await ticketService?.create(ticketData);
      setShowCreateModal(false);
      await fetchTickets();
    } catch (err) {
      console.error('Error creating ticket:', err);
      throw err;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      open: 'Açık',
      in_progress: 'İşlemde',
      resolved: 'Çözüldü',
      closed: 'Kapatıldı'
    };
    return texts?.[status] || status;
  };

  const getCategoryText = (category) => {
    const texts = {
      technical_issues: 'Teknik Sorun',
      billing: 'Faturalama',
      account: 'Hesap',
      feature_request: 'Özellik İsteği',
      bug_report: 'Hata Bildirimi',
      general_inquiry: 'Genel Soru'
    };
    return texts?.[category] || category;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-600',
      medium: 'text-blue-600',
      high: 'text-orange-600',
      urgent: 'text-red-600'
    };
    return colors?.[priority] || 'text-gray-600';
  };

  const getPriorityText = (priority) => {
    const texts = {
      low: 'Düşük',
      medium: 'Orta',
      high: 'Yüksek',
      urgent: 'Acil'
    };
    return texts?.[priority] || priority;
  };

  const filteredTickets = tickets?.filter(ticket => {
    if (filterStatus !== 'all' && ticket?.status !== filterStatus) return false;
    if (filterCategory !== 'all' && ticket?.category !== filterCategory) return false;
    return true;
  });

  const stats = {
    total: tickets?.length || 0,
    open: tickets?.filter(t => t?.status === 'open')?.length || 0,
    inProgress: tickets?.filter(t => t?.status === 'in_progress')?.length || 0,
    resolved: tickets?.filter(t => t?.status === 'resolved')?.length || 0
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-7xl pt-[76px]">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2 flex items-center gap-3">
                <Icon name="Headphones" size={36} className="text-primary" />
                Destek Talepleri
              </h1>
              <p className="text-muted-foreground">Destek taleplerinizi oluşturun ve takip edin</p>
            </div>
            <Button
              variant="default"
              size="default"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowCreateModal(true)}
            >
              Yeni Talep Oluştur
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-card rounded-xl shadow-organic p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Toplam Talep</p>
                <p className="text-3xl font-bold text-foreground">{stats?.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Icon name="FileText" size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow-organic p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Açık</p>
                <p className="text-3xl font-bold text-blue-600">{stats?.open}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Icon name="AlertCircle" size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow-organic p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">İşlemde</p>
                <p className="text-3xl font-bold text-yellow-600">{stats?.inProgress}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Icon name="Clock" size={24} className="text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow-organic p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Çözüldü</p>
                <p className="text-3xl font-bold text-green-600">{stats?.resolved}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-xl shadow-organic p-4 mb-6 border border-border">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-2">Durum</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Tümü</option>
                <option value="open">Açık</option>
                <option value="in_progress">İşlemde</option>
                <option value="resolved">Çözüldü</option>
                <option value="closed">Kapatıldı</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-2">Kategori</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Tümü</option>
                <option value="technical_issues">Teknik Sorun</option>
                <option value="billing">Faturalama</option>
                <option value="account">Hesap</option>
                <option value="feature_request">Özellik İsteği</option>
                <option value="bug_report">Hata Bildirimi</option>
                <option value="general_inquiry">Genel Soru</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tickets List */}
        <div className="bg-card rounded-xl shadow-organic border border-border overflow-hidden">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Talepler yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" size={32} className="text-red-600" />
              </div>
              <p className="text-red-600 mb-4">{error}</p>
              <Button variant="outline" size="default" onClick={fetchTickets}>
                Tekrar Dene
              </Button>
            </div>
          ) : filteredTickets?.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Inbox" size={32} className="text-muted-foreground" />
              </div>
              <p className="text-muted-foreground mb-4">Henüz destek talebi bulunmuyor</p>
              <Button
                variant="outline"
                size="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowCreateModal(true)}
              >
                İlk Talebinizi Oluşturun
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredTickets?.map((ticket) => (
                <div
                  key={ticket?.id}
                  className="p-6 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-mono text-muted-foreground">#{ticket?.ticketNumber}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(ticket?.status)}`}>
                          {getStatusText(ticket?.status)}
                        </span>
                        <span className={`text-xs font-semibold ${getPriorityColor(ticket?.priority)}`}>
                          {getPriorityText(ticket?.priority)}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">{ticket?.subject}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{ticket?.description}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="Tag" size={14} />
                          <span>{getCategoryText(ticket?.category)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" size={14} />
                          <span>{new Date(ticket?.createdAt)?.toLocaleDateString('tr-TR')}</span>
                        </div>
                        {ticket?.updatedAt && (
                          <div className="flex items-center gap-1">
                            <Icon name="Clock" size={14} />
                            <span>Güncellendi: {new Date(ticket?.updatedAt)?.toLocaleDateString('tr-TR')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" iconName="ChevronRight">
                      Detaylar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      {/* Create Ticket Modal */}
      {showCreateModal && (
        <CreateTicketModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTicket}
        />
      )}
      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdate={fetchTickets}
        />
      )}
    </div>
  );
};

export default SupportTicketSystemUserInterface;